<template>
  <div
    class="sp-card"
    :class="{ 'locale-zh-CN': isZhLocale }"
    @click="$emit('click', item)"
  >
    <div class="cover-wrap">
      <img class="cover" :src="coverSrc" alt="" @error="onCoverError" />
    </div>
    <div class="body">
      <h3 class="title" :title="item.title">{{ item.title || '' }}</h3>
      <div class="feature-row" :title="featureText">
        <img v-if="starIcon" class="star-ico" :src="starIcon" alt="" @error="onImgError" />
        <span class="feature-text">{{ featureText }}</span>
      </div>
      <div class="meta">
        <span class="tag">{{ displayTag }}</span>
        <div class="provider" :title="providerFull">
          <img v-if="unitIcon" class="unit-ico" :src="unitIcon" alt="" @error="onImgError" />
          <span class="provider-text">
            <span class="provider-label">{{ labels.providerLabel }}</span>
            <span class="provider-name">{{ displayProvider }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getServiceProductNewConfig } from '@/config/serviceProductNewConfig'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getCurrentLocale } from '@/utils/i18nText'

const EMPTY_TIP = '待接口提供'

export default {
  name: 'ServiceProductCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      coverFailed: false
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    config() {
      return getServiceProductNewConfig(getCurrentLocale())
    },
    labels() {
      return this.config.card || {}
    },
    emptyTip() {
      return this.labels.emptyTip || EMPTY_TIP
    },
    fallbackCover() {
      return getSafeImage(this.labels.coverFallback)
    },
    starIcon() {
      return getSafeImage(this.labels.starIcon)
    },
    unitIcon() {
      return getSafeImage(this.labels.unitIcon)
    },
    coverSrc() {
      if (this.coverFailed) return this.fallbackCover
      const src = getSafeImage(this.item.image)
      return src || this.fallbackCover
    },
    featureText() {
      const list = Array.isArray(this.item.features)
        ? this.item.features.map((f) => this.normalizeText(f)).filter(Boolean)
        : []
      if (!list.length) return ''
      return list.join(' ')
    },
    displayTag() {
      return this.normalizeText(this.item.tag || this.item.category)
    },
    displayProvider() {
      return this.normalizeText(this.item.provider)
    },
    providerFull() {
      return `${this.labels.providerLabel || ''}${this.displayProvider}`
    }
  },
  watch: {
    'item.image'() {
      this.coverFailed = false
    },
    'item.id'() {
      this.coverFailed = false
    }
  },
  methods: {
    normalizeText(val) {
      const text = String(val == null ? '' : val).trim()
      if (!text || text === this.emptyTip || text === EMPTY_TIP) return ''
      return text
    },
    onCoverError(e) {
      this.coverFailed = true
      handleImgError(e)
    },
    onImgError(e) {
      handleImgError(e)
    }
  }
}
</script>

<style lang="scss" scoped>
.sp-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #ebe6d8;
  border-radius: 6px;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 18px rgba(160, 144, 60, 0.12);

    .title {
      color: #8c7b21;
    }
  }
}

.cover-wrap {
  width: 100%;
  height: 168px;
  background: #f5f5f5;
  overflow: hidden;
}

.cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px 16px 18px;
  box-sizing: border-box;
}

.title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #222;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feature-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  min-width: 0;
  flex: 1;
}

.star-ico {
  width: 12px;
  height: 12px;
  margin-top: 4px;
  flex-shrink: 0;
  object-fit: contain;
}

.feature-text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #a0903c;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  min-width: 0;
}

.tag {
  flex-shrink: 0;
  padding: 2px 8px;
  border: 1px solid rgba(160, 144, 60, 0.55);
  border-radius: 2px;
  font-size: 12px;
  color: #a0903c;
  background: transparent;
  line-height: 1.4;
}

.provider {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.unit-ico {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  object-fit: contain;
}

.provider-text {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provider-label {
  color: #666;
}

.provider-name {
  color: #555;
}
.locale-zh-CN {
  .title {
    font-size: 20px;
  }

  .feature-text {
    font-size: 15px;
  }

  .tag,
  .provider-text {
    font-size: 14px;
  }

  .star-ico {
    width: 14px;
    height: 14px;
  }

  .unit-ico {
    width: 16px;
    height: 16px;
  }
}
</style>
